<template>
  <div>
    <div v-if="!allowed" class="p-m-3 pse-note error"><i class="pi pi-ban"></i><span>You are not allowed to create a new enquiry.</span></div>
    <!-- Design B: today's "Enquiry Details" dialog, with the project, the phase and the Excel job fields added in its style -->
    <Dialog v-else :visible="true" :style="{ width: '800px' }" :modal="true" class="p-fluid pse-b" @update:visible="close">
      <template #header>
        <h5>Enquiry Details</h5>
      </template>

      <LayoutCompareNote />
      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label for="pseBProject">Project *</label>
          <div class="p-inputgroup">
            <InputText id="pseBProject" :value="project ? project.name : ''" placeholder="Click button to search for project" :disabled="true"
              :class="{ 'p-invalid': submitted && !project }" />
            <Button icon="pi pi-search" class="p-button-warning" :disabled="fixedJob" @click="projectSearch = true" />
          </div>
          <small class="p-invalid" v-if="submitted && !project">Project is required.</small>
        </div>
        <div class="p-field p-col">
          <label for="pseBPhase">Phase</label>
          <div class="p-inputgroup">
            <Dropdown id="pseBPhase" v-model="phaseId" :options="phaseOptions" optionLabel="label" optionValue="phaseId"
              placeholder="Main project" :disabled="!project || fixedJob" />
            <Button icon="pi pi-plus" class="p-button-warning" title="Add a phase" :disabled="!project || fixedJob" @click="phaseDialog = true" />
          </div>
        </div>
      </div>
      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label>Postcode Area</label>
          <InputText :value="project ? project.postcodeArea : ''" :disabled="true" />
        </div>
        <div class="p-field p-col">
          <label>Post Code</label>
          <InputText :value="project ? project.postCode : ''" :disabled="true" />
        </div>
        <div class="p-field p-col">
          <label>City/Town</label>
          <InputText :value="project ? project.town : ''" :disabled="true" />
        </div>
      </div>
      <div v-if="sharedEnqId" class="p-mb-3 job-locked-note">
        <i class="pi pi-lock"></i>
        This enquiry is already in the CRM ({{ existingBidCount }} quotation{{ existingBidCount === 1 ? '' : 's' }}): its details are shared,
        so this adds a quotation to it.
      </div>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label for="pseBidCust">Customer *</label>
          <AutoComplete id="pseBidCust" v-model="customer" :suggestions="customerSuggestions" field="custName" placeholder="Type 3+ letters of the customer"
            :minLength="3" :delay="350" @complete="findCustomers($event.query)" @item-select="onCustomer($event.value)"
            :class="{ 'p-invalid': shown('customerId') }">
            <template #item="slotProps">
              {{ slotProps.item.custName }}
              <span class="pse-muted">{{ [slotProps.item.accountCode, slotProps.item.postCode, slotProps.item.town].filter(Boolean).join(' · ') }}</span>
            </template>
          </AutoComplete>
        </div>
        <div class="p-field p-col">
          <label for="pseBidContact">Contact{{ req('contactId') ? ' *' : '' }}</label>
          <Dropdown id="pseBidContact" v-model="contact" :options="contacts" optionLabel="contactName" dataKey="contactId" :showClear="true"
            placeholder="Select Contact" :disabled="!customerId" @change="onContact" :class="{ 'p-invalid': shown('contactId') }" />
        </div>
      </div>
      <div class="p-formgrid p-grid">
        <div class="p-field p-col p-sm-6">
          <label for="pseBidBranch">Branch{{ req('branchId') ? ' *' : '' }}</label>
          <Dropdown id="pseBidBranch" v-model="branchId" :options="branches" optionLabel="branchName" optionValue="branchId"
            placeholder="Select Branch" :disabled="!customerId || onlyUserBranch" :class="{ 'p-invalid': shown('branchId') }" />
          <small v-if="noOwnBranch" class="p-invalid">This customer is not set up for your branch, so you cannot add it.</small>
        </div>
        <div class="p-field p-col p-sm-6">
          <label for="pseBidPt">Product Type{{ req('productTypeId') ? ' *' : '' }}</label>
          <Dropdown id="pseBidPt" v-model="productTypeId" :options="productTypes" optionLabel="productTypeName" optionValue="id" :filter="true"
            placeholder="Select Product Type" :class="{ 'p-invalid': shown('productTypeId') }" />
        </div>
      </div>
      <div class="p-formgrid p-grid">
        <div class="p-field p-col p-sm-6">
          <label for="pseBidPhone">Phone No{{ req('phoneNo') ? ' *' : '' }}</label>
          <InputText id="pseBidPhone" v-model.trim="phoneNo" :class="{ 'p-invalid': shown('phoneNo') }" />
        </div>
        <div class="p-field p-col p-sm-6">
          <label for="pseBidEmail">Email{{ req('email') ? ' *' : '' }}</label>
          <InputText id="pseBidEmail" v-model.trim="email" :class="{ 'p-invalid': shown('email') }" />
        </div>
      </div>
      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label>Sales Exec</label>
          <InputText :value="salesExecName" :disabled="true" />
        </div>
        <div class="p-field p-col">
          <label for="pseBidType">Enquiry Type{{ req('projectTypeId') ? ' *' : '' }}</label>
          <Dropdown id="pseBidType" v-model="projectTypeId" :options="projectTypes" optionLabel="projTypeName" optionValue="id" :showClear="true"
            placeholder="Select Enquiry Type" :class="{ 'p-invalid': shown('projectTypeId') }" />
        </div>
      </div>
      <div class="p-formgrid p-grid">
        <div class="p-field p-col">
          <label for="pseBidChance">Chance of Order{{ req('chance') ? ' *' : '' }}</label>
          <Dropdown id="pseBidChance" v-model="chance" :options="chances" optionLabel="label" optionValue="value" :showClear="true"
            placeholder="A / B / C" :class="{ 'p-invalid': shown('chance') }" />
        </div>
        <div class="p-field p-col">
          <label for="pseBidMargin">Branch Margin (%){{ req('branchMargin') ? ' *' : '' }}</label>
          <InputNumber id="pseBidMargin" v-model="branchMargin" mode="decimal" :minFractionDigits="0" :maxFractionDigits="2" suffix=" %"
            :class="{ 'p-invalid': shown('branchMargin') }" />
        </div>
        <div class="p-field p-col">
          <label for="pseBidChaser">Who will be chasing{{ req('chaserUserId') ? ' *' : '' }}</label>
          <Dropdown id="pseBidChaser" v-model="chaserUserId" :options="users" optionLabel="fullName" optionValue="id" :filter="true" :showClear="true"
            placeholder="Select User" :class="{ 'p-invalid': shown('chaserUserId') }" />
        </div>
      </div>

      <div v-if="checking" class="pse-note info"><i class="pi pi-spin pi-spinner"></i><span>Checking this customer against the enquiry...</span></div>
      <div v-else-if="check && check.result === 'WARN' && confirmed" class="pse-muted pse-confirmed">
        <i class="pi pi-check"></i> {{ customer && customer.custName }} already has a quotation on this enquiry: a new quotation will be added, with its own quote number.
      </div>

      <h6 class="pse-b-section">Enquiry details <span class="pse-muted">(the enquiry sheet: entered once, shared by every quotation)</span></h6>
      <JobFields v-model="job" :settings="settings" :submitted="submitted && !sharedEnqId" :disabled="!!sharedEnqId" colClass="p-col-6" />

      <h6 class="pse-b-section">Attachments</h6>
      <AttachmentsByCategory v-if="!sharedEnqId" ref="attachments" :sharedEnqId="null" />
      <AttachmentsByCategory v-else :key="sharedEnqId" :sharedEnqId="sharedEnqId" />

      <div v-if="submitted && allMissing.length" class="pse-note error pse-missing">
        <i class="pi pi-times-circle"></i><span>Not saved. Please fill in: <strong>{{ allMissing.join(', ') }}</strong>.</span>
      </div>
      <div v-if="error" class="pse-note error"><i class="pi pi-times-circle"></i><span>{{ error }}</span></div>

      <template #footer>
        <Button label="Cancel" icon="pi pi-times" class="p-button-danger" style="width: 100px;" @click="close" />
        <Button v-if="canOffer" label="Save" icon="pi pi-check" class="p-button-success pse-save" style="width: 100px;" :loading="saving" @click="save" />
      </template>
    </Dialog>

    <DuplicateQuestion :visible="questionOpen" :check="check" :custName="customer && customer.custName ? customer.custName : ''"
      @newVersion="answerNewVersion" @anyway="answerAnyway" @change="answerChangeCustomer" />

    <Dialog header="Search Project" :visible="projectSearch" :modal="true" :style="{ width: '900px' }" @update:visible="projectSearch = $event">
      <ProjectPicker enquiryAction="Add a quotation" @project="onProject" @sharedEnquiry="onExistingEnquiry" />
    </Dialog>

    <Dialog header="New Phase" :visible="phaseDialog" :modal="true" :style="{ width: '420px' }" class="p-fluid" @update:visible="phaseDialog = $event">
      <div class="p-formgrid p-grid">
        <div class="p-field p-col-4">
          <label for="pseBPhNo">Phase No *</label>
          <InputText id="pseBPhNo" v-model.trim="newPhase.phaseNo" placeholder="e.g. 2A" />
        </div>
        <div class="p-field p-col-8">
          <label for="pseBPhDesc">Description</label>
          <InputText id="pseBPhDesc" v-model.trim="newPhase.description" placeholder="e.g. Annex block" />
        </div>
      </div>
      <small class="pse-muted">The main project counts as the same enquiry as each of its phases for the duplicate check.</small>
      <template #footer>
        <Button label="Add" icon="pi pi-check" class="p-button-success" style="width: 100px;" :disabled="!newPhase.phaseNo" :loading="busy" @click="addPhase" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import ProjectPicker from './ProjectPicker.vue'
import JobFields from './JobFields.vue'
import AttachmentsByCategory from './AttachmentsByCategory.vue'
import DuplicateQuestion from './DuplicateQuestion.vue'
import LayoutCompareNote from './LayoutCompareNote.vue'
import jobQuoteForm from './jobQuoteForm'

// Design B of the New Enquiry form (Mohammed 18/10, to compare with the stepped design A): today's dialog, one Save.
export default {
  name: 'NewEnquiryClassic',
  components: { ProjectPicker, JobFields, AttachmentsByCategory, DuplicateQuestion, LayoutCompareNote },
  mixins: [jobQuoteForm]
}
</script>

<style scoped>
@import './sharedEnquiry.css';
.pse-b-section { margin: 18px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #dee2e6; }
.job-locked-note { background: #fff8e1; color: #8a5a00; border: 1px solid #ffcc80; border-radius: 4px; padding: 8px 12px; font-size: 13px; }
</style>

<style>
/* not scoped: the Dialog's own element carries no scope attribute. Today's form scrolls inside its dialog; the longer
   form must too, on a laptop screen. */
.pse-b .p-dialog-content { max-height: 75vh; overflow-y: auto; }
</style>
